import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, MessageSquare, Image, Megaphone, CreditCard, FileText,
  Wrench, Star, MapPin, HelpCircle, Mail, Smartphone, ListChecks, Plug, ScrollText,
  LogOut, Menu, X, Loader2, Phone, UserSquare, KanbanSquare, Shield, DownloadCloud,
  Receipt, DollarSign, Settings, ClipboardList
} from "lucide-react";
import { api, getToken, clearToken } from "@/lib/api";
import { inputCls, labelCls } from "./AdminUI";
import { InstallButton } from "@/components/site/PwaBits";

import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import Customers from "./pages/Customers";
import Quotes from "./pages/Quotes";
import Jobs from "./pages/Jobs";
import FollowUps from "./pages/FollowUps";
import AdminUsers from "./pages/AdminUsers";
import DataExport from "./pages/DataExport";
import Invoices from "./pages/Invoices";
import Payments from "./pages/Payments";
import PaymentRequests from "./pages/PaymentRequests";
import BillingSettings from "./pages/BillingSettings";
import Conversations from "./pages/Conversations";
import Media from "./pages/Media";
import AdMaker from "./pages/AdMaker";
import Financing from "./pages/Financing";
import Content from "./pages/Content";
import ServicesAdmin from "./pages/Services";
import ReviewsAdmin from "./pages/Reviews";
import ServiceAreasAdmin from "./pages/ServiceAreas";
import FaqsAdmin from "./pages/Faqs";
import Templates from "./pages/Templates";
import Checklist from "./pages/Checklist";
import Integrations from "./pages/Integrations";
import AuditLogs from "./pages/AuditLogs";
import PwaStatus from "./pages/PwaStatus";
import EmailSettings from "./pages/EmailSettings";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, section: "dashboard" },
  { to: "/admin/leads", label: "Leads", icon: Users, section: "leads" },
  { to: "/admin/crm", label: "CRM", icon: KanbanSquare, section: "crm" },
  { to: "/admin/quotes", label: "Quotes", icon: FileText, section: "quotes" },
  { to: "/admin/jobs", label: "Jobs", icon: Wrench, section: "jobs" },
  { to: "/admin/follow-ups", label: "Follow-Ups", icon: ListChecks, section: "followups" },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt, section: "invoices" },
  { to: "/admin/payments", label: "Payments", icon: DollarSign, section: "payments" },
  { to: "/admin/payment-requests", label: "Payment Requests", icon: ClipboardList, section: "payment_requests" },
  { to: "/admin/billing-settings", label: "Billing Settings", icon: Settings, section: "billing_settings" },
  { to: "/admin/conversations", label: "Ghost AI Chats", icon: MessageSquare, section: "conversations" },
  { to: "/admin/media", label: "Media Center", icon: Image, section: "media" },
  { to: "/admin/admaker", label: "Ad Maker", icon: Megaphone, section: "admaker" },
  { to: "/admin/financing", label: "Client Financing", icon: CreditCard, section: "financing" },
  { to: "/admin/content", label: "Website Content", icon: FileText, section: "content" },
  { to: "/admin/services", label: "Services", icon: Wrench, section: "services" },
  { to: "/admin/reviews", label: "Reviews", icon: Star, section: "reviews" },
  { to: "/admin/service-areas", label: "Service Areas", icon: MapPin, section: "service_areas" },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, section: "faqs" },
  { to: "/admin/email-templates", label: "Email Templates", icon: Mail, section: "email_templates" },
  { to: "/admin/email-settings", label: "Email Settings", icon: Mail, section: "email_settings" },
  { to: "/admin/sms-templates", label: "SMS Templates", icon: Smartphone, section: "sms_templates" },
  { to: "/admin/checklist", label: "Setup Checklist", icon: ListChecks, section: "checklist" },
  { to: "/admin/integrations", label: "Integrations", icon: Plug, section: "integrations" },
  { to: "/admin/pwa", label: "PWA Status", icon: Smartphone, section: "pwa" },
  { to: "/admin/users", label: "Admin Users", icon: Shield, section: "admin_users" },
  { to: "/admin/export", label: "Data Export", icon: DownloadCloud, section: "data_export" },
  { to: "/admin/audit", label: "Activity Log", icon: ScrollText, section: "activity_log" },
];

const canSee = (admin, section) => {
  const secs = admin?.sections || [];
  return secs.includes("*") || secs.includes(section);
};

const Guard = ({ admin, section, children }) =>
  canSee(admin, section) ? children : <Navigate to="/admin" replace />;

function ChangePasswordGate({ onDone }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center px-4">
      <form onSubmit={submit} className="bg-white rounded-2xl p-7 w-full max-w-md card-shadow-lg space-y-4" data-testid="change-password-gate">
        <h1 className="font-head font-extrabold text-xl uppercase text-[var(--navy)]">Change Your Temporary Password</h1>
        <p className="text-sm text-gray-500">For security, please set a new password before continuing.</p>
        <div><label className={labelCls}>Current Password</label>
          <input className={inputCls} type="password" value={cur} onChange={(e) => setCur(e.target.value)} required /></div>
        <div><label className={labelCls}>New Password (min 8 chars)</label>
          <input className={inputCls} type="password" value={nw} onChange={(e) => setNw(e.target.value)} required data-testid="new-password" /></div>
        <button disabled={busy} className="w-full bg-[var(--red)] text-white font-semibold py-2.5 rounded-md" data-testid="change-password-submit">
          {busy ? "Saving…" : "Update Password"}
        </button>
      </form>
    </div>
  );
}

export default function AdminApp() {
  const nav = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = () => {
    if (!getToken()) { nav("/admin/login"); return; }
    api.get("/auth/me").then((r) => setAdmin(r.data)).catch(() => nav("/admin/login")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const logout = () => { clearToken(); nav("/admin/login"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[var(--action)]" /></div>;
  if (!admin) return <Navigate to="/admin/login" />;
  if (admin.must_change_password) return <ChangePasswordGate onDone={() => setAdmin({ ...admin, must_change_password: false })} />;

  return (
    <div className="min-h-screen bg-[var(--surface)] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[var(--navy)] text-white flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-5 py-4 flex items-center gap-3 border-b border-white/10">
          <img src="/brand/logo.png" alt="" className="h-10 w-10 rounded-lg" />
          <div className="leading-tight"><p className="font-head font-bold uppercase text-sm">Veterans PW</p><p className="text-[10px] text-white/50">Admin Portal</p></div>
        </div>
        <nav className="flex-1 overflow-y-auto no-scrollbar py-3 px-2 space-y-0.5">
          {NAV.filter((n) => canSee(admin, n.section)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-[var(--red)] text-white font-semibold" : "text-white/70 hover:bg-white/10"}`}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <n.icon className="w-4.5 h-4.5 shrink-0" style={{ width: 18, height: 18 }} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="mb-2"><InstallButton className="w-full" /></div>
          <p className="text-[11px] text-white/40 px-2 mb-1 truncate">{admin.email}</p>
          <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/70 hover:bg-white/10" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Log Out
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-gray-200 h-14 flex items-center px-4 gap-3 sticky top-0 z-20">
          <button className="lg:hidden" onClick={() => setOpen(!open)}>{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          <a href="/" target="_blank" rel="noreferrer" className="text-sm text-[var(--action)] hover:underline ml-auto">View Website ↗</a>
          <a href="tel:+13213768744" className="hidden sm:flex items-center gap-1 text-sm text-[var(--navy)] font-semibold"><Phone className="w-4 h-4 text-[var(--red)]" /> (321) 376-8744</a>
        </header>
        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Guard admin={admin} section="leads"><Leads /></Guard>} />
            <Route path="customers" element={<Navigate to="/admin/crm?view=customers" replace />} />
            <Route path="crm-pipeline" element={<Navigate to="/admin/crm?view=pipeline" replace />} />
            <Route path="crm" element={<Guard admin={admin} section="crm"><Customers canDelete={admin.is_super} /></Guard>} />
            <Route path="quotes" element={<Guard admin={admin} section="quotes"><Quotes canDelete={admin.is_super} /></Guard>} />
            <Route path="jobs" element={<Guard admin={admin} section="jobs"><Jobs canDelete={admin.is_super} /></Guard>} />
            <Route path="follow-ups" element={<Guard admin={admin} section="followups"><FollowUps /></Guard>} />
            <Route path="invoices" element={<Guard admin={admin} section="invoices"><Invoices canVoid={admin.is_super || admin.role === "Administrator"} /></Guard>} />
            <Route path="payments" element={<Guard admin={admin} section="payments"><Payments canRefund={admin.is_super || admin.role === "Administrator"} /></Guard>} />
            <Route path="payment-requests" element={<Guard admin={admin} section="payment_requests"><PaymentRequests /></Guard>} />
            <Route path="billing-settings" element={<Guard admin={admin} section="billing_settings"><BillingSettings isSuper={admin.is_super} /></Guard>} />
            <Route path="users" element={<Guard admin={admin} section="admin_users"><AdminUsers /></Guard>} />
            <Route path="export" element={<Guard admin={admin} section="data_export"><DataExport /></Guard>} />
            <Route path="conversations" element={<Guard admin={admin} section="conversations"><Conversations /></Guard>} />
            <Route path="media" element={<Guard admin={admin} section="media"><Media /></Guard>} />
            <Route path="admaker" element={<Guard admin={admin} section="admaker"><AdMaker /></Guard>} />
            <Route path="financing" element={<Guard admin={admin} section="financing"><Financing /></Guard>} />
            <Route path="content" element={<Guard admin={admin} section="content"><Content /></Guard>} />
            <Route path="services" element={<Guard admin={admin} section="services"><ServicesAdmin /></Guard>} />
            <Route path="reviews" element={<Guard admin={admin} section="reviews"><ReviewsAdmin /></Guard>} />
            <Route path="service-areas" element={<Guard admin={admin} section="service_areas"><ServiceAreasAdmin /></Guard>} />
            <Route path="faqs" element={<Guard admin={admin} section="faqs"><FaqsAdmin /></Guard>} />
            <Route path="email-templates" element={<Guard admin={admin} section="email_templates"><Templates kind="email" /></Guard>} />
            <Route path="email-settings" element={<Guard admin={admin} section="email_settings"><EmailSettings /></Guard>} />
            <Route path="sms-templates" element={<Guard admin={admin} section="sms_templates"><Templates kind="sms" /></Guard>} />
            <Route path="checklist" element={<Guard admin={admin} section="checklist"><Checklist /></Guard>} />
            <Route path="integrations" element={<Guard admin={admin} section="integrations"><Integrations /></Guard>} />
            <Route path="pwa" element={<Guard admin={admin} section="pwa"><PwaStatus /></Guard>} />
            <Route path="audit" element={<Guard admin={admin} section="activity_log"><AuditLogs /></Guard>} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
